/**
 * Single source of truth for the site's routes.
 *
 * Consumed by:
 *  - App.tsx                  (client router)
 *  - prerender/entry-server   (build-time HTML per route)
 *  - vite.config.ts           (sitemap)
 *
 * Adding a page means adding it here once.
 */
import { HEATER_CATALOG_DATA } from './products/catalog/data';
import { BUSINESS_SCENARIO_PATHS } from './seo/business';
import { COMPARISON_PATHS } from './seo/comparisons';
import { GUIDE_PATH } from './seo/guide';
import { LOCALIZED_LANGUAGES, localizePath } from './i18n/supported';
import { TRANSLATED_PATHS } from './i18n/translatedPaths';

export type PageName =
  | 'HomePage'
  | 'ProductsPage'
  | 'ProductDetailPage'
  | 'TechnologyPage'
  | 'InstallationsPage'
  | 'AboutPage'
  | 'ContactPage'
  | 'InfraredHeatersPage'
  | 'PortableHeatersPage'
  | 'InstallationGuidePage'
  | 'WarrantyPolicyPage'
  | 'FAQPage'
  | 'PrivacyPolicyPage'
  | 'TermsOfServicePage'
  | 'NewsPage'
  | 'NewsArticlePage'
  | 'BusinessScenarioPage'
  | 'ComparisonPage'
  | 'HospitalityGuidePage'
  | 'OemOdmPage'
  | 'CompliancePage'
  | 'SizingCalculatorPage';

export interface RouteDef {
  path: string;
  page: PageName;
  /** When set the route only redirects (rendered as <Navigate replace />). */
  redirectTo?: string;
}

/** Every route the client router serves. */
const BASE_ROUTES: RouteDef[] = [
  { path: '/', page: 'HomePage' },
  { path: '/products', page: 'ProductsPage' },
  { path: '/gas-heaters', page: 'ProductsPage' },
  // Legacy product detail route (client-side redirect to the canonical URL)
  { path: '/products/:id', page: 'ProductDetailPage' },
  { path: '/product/:category/:id', page: 'ProductDetailPage' },
  { path: '/infrared-heaters', page: 'InfraredHeatersPage' },
  { path: '/portable-heaters', page: 'PortableHeatersPage' },
  { path: '/infrared-heaters/products', page: 'InfraredHeatersPage', redirectTo: '/infrared-heaters' },
  { path: '/portable-heaters/products', page: 'PortableHeatersPage', redirectTo: '/portable-heaters' },
  { path: '/technology', page: 'TechnologyPage' },
  { path: '/installations', page: 'InstallationsPage' },
  { path: '/about', page: 'AboutPage' },
  { path: '/contact', page: 'ContactPage' },
  { path: '/installation-guide', page: 'InstallationGuidePage' },
  { path: '/warranty-policy', page: 'WarrantyPolicyPage' },
  { path: '/faq', page: 'FAQPage' },
  { path: '/news', page: 'NewsPage' },
  { path: '/news/:slug', page: 'NewsArticlePage' },
  { path: '/privacy-policy', page: 'PrivacyPolicyPage' },
  { path: '/terms-of-service', page: 'TermsOfServicePage' },
  // SEO / GEO landing pages — content lives in src/seo/*
  { path: '/business/:slug', page: 'BusinessScenarioPage' },
  { path: '/compare/:slug', page: 'ComparisonPage' },
  { path: GUIDE_PATH, page: 'HospitalityGuidePage' },
  { path: '/oem-odm', page: 'OemOdmPage' },
  { path: '/compliance', page: 'CompliancePage' },
  { path: '/tools/heater-sizing-calculator', page: 'SizingCalculatorPage' },
];

/** Concrete URLs for the parameterised SEO landing pages (used by prerender + sitemap). */
export const LANDING_PATHS: string[] = [
  ...BUSINESS_SCENARIO_PATHS,
  ...COMPARISON_PATHS,
  GUIDE_PATH,
  '/oem-odm',
  '/compliance',
  '/tools/heater-sizing-calculator',
];

/**
 * Language-prefixed copies of the translated pages, plus the product route so
 * /es/product/… resolves on the client. English stays unprefixed at the root.
 */
export const LOCALIZED_ROUTES: RouteDef[] = LOCALIZED_LANGUAGES.flatMap((language) => [
  ...TRANSLATED_PATHS.map((path) => {
    const base = BASE_ROUTES.find((route) => route.path === path && !route.redirectTo);
    if (!base) throw new Error(`[routes] no base route for translated path ${path}`);
    return { path: localizePath(language, path), page: base.page };
  }),
  { path: `/${language}/product/:category/:id`, page: 'ProductDetailPage' as PageName },
]);

export const ROUTES: RouteDef[] = [...BASE_ROUTES, ...LOCALIZED_ROUTES];

/** Infrared heater product slugs (categoryKey: 'infraredHeater'). */
export const INFRARED_PRODUCT_SLUGS = [
  '13a-series',
  '13b-series',
  '13c-series',
  '22b-series',
  '13w-series',
  '14a-series',
  '14b-series',
  '23w-series',
  '23a-series',
  '25w-series',
];

/** Portable heater product slugs (categoryKey: 'portableHeater'). */
export const PORTABLE_PRODUCT_SLUGS = [
  'jhl020a',
  'jhl023',
  'jhl023-05',
  'jhl021-s',
  'jhl004-b',
  'jhl001-bz',
  'jhd028-b',
  'jhg019-b',
  'jh-104',
  'jh-106',
  'jh-3000',
];

/** Catalog-driven product URLs (gas or portable, depending on the record). */
export const CATALOG_PRODUCT_PATHS: string[] = HEATER_CATALOG_DATA.map(
  (product) => `/product/${product.gas ? 'gas-heaters' : 'portable-heaters'}/${product.model.toLowerCase()}`
);

export const INFRARED_PRODUCT_PATHS = INFRARED_PRODUCT_SLUGS.map((slug) => `/product/infrared-heaters/${slug}`);
export const PORTABLE_PRODUCT_PATHS = PORTABLE_PRODUCT_SLUGS.map((slug) => `/product/portable-heaters/${slug}`);

export const PRODUCT_PATHS: string[] = Array.from(
  new Set([...CATALOG_PRODUCT_PATHS, ...INFRARED_PRODUCT_PATHS, ...PORTABLE_PRODUCT_PATHS])
);

/** The ten infrared series pages, prefixed for each translated language. */
export const LOCALIZED_PRODUCT_PATHS: string[] = LOCALIZED_LANGUAGES.flatMap((language) =>
  INFRARED_PRODUCT_PATHS.map((path) => localizePath(language, path))
);

/** Non-parameter routes that render real content (no redirects). */
export const STATIC_PATHS: string[] = ROUTES.filter(
  (route) => !route.redirectTo && !/[:*[\]]/.test(route.path)
).map((route) => route.path);

/** Legal pages exist and are crawlable, but are kept out of the sitemap. */
export const LOW_VALUE_PATHS = ['/privacy-policy', '/terms-of-service'];

/**
 * Routes that are prerendered to static HTML at build time.
 * `/news/:slug` is excluded: those articles are fetched from R2 at runtime.
 */
export const PRERENDER_PATHS: string[] = Array.from(
  new Set([
    ...STATIC_PATHS.filter((path) => !LOW_VALUE_PATHS.includes(path)),
    ...LOW_VALUE_PATHS,
    ...PRODUCT_PATHS,
    ...LANDING_PATHS,
    ...LOCALIZED_PRODUCT_PATHS,
  ])
);

/** Sitemap contents: everything prerendered except the legal pages. */
export const SITEMAP_PATHS: string[] = PRERENDER_PATHS.filter((path) => !LOW_VALUE_PATHS.includes(path));
